/**
 * Coverage Audit Page — /coverage/audit
 *
 * Per-jurisdiction, per-stage data completeness view.
 * Confirms every pipeline stage (extract → classify → chunk → embed)
 * actually has data for each jurisdiction — data-integrity, not
 * content-analytics (that's /coverage).
 *
 * ACCESS: SYSTEM_ADMIN only.
 *   - Client-side guard below redirects the UI.
 *   - ⚠️  All data endpoints built into this page MUST also enforce
 *     SYSTEM_ADMIN server-side via requireRole middleware. This page
 *     reads raw pipeline tables across all jurisdictions; a client-
 *     side-only guard would allow crafted requests to pull
 *     cross-jurisdiction data. See: provision and admin endpoint
 *     patterns for the required server-side guard shape.
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useUser } from '@/hooks/useUser';
import { usePageTitle } from '@/hooks/usePageTitle';
import { CoverageAuditGrid } from '@/components/admin/CoverageAuditGrid';

export const CoverageAuditPage: React.FC = () => {
  usePageTitle('Coverage Audit');
  const { user, loading } = useUser();

  // SYSTEM_ADMIN only — ORG_OWNER intentionally excluded.
  // This page queries raw pipeline tables across ALL jurisdictions.
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          <p className="text-sm text-gray-500">Loading…</p>
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'SYSTEM_ADMIN') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Coverage Audit</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Data-integrity check: confirms every pipeline stage actually has
            data for each jurisdiction, and surfaces silent gaps before a user
            query hits them.
          </p>
        </div>
      </div>

      {/* ── Dashboard ── */}
      <CoverageAuditGrid />
    </div>
  );
};
